/**
 * /erp/settings/warehouses — manage warehouses (Manager+).
 *
 * Single-page CRUD: header create form + list of existing warehouses
 * with inline rename / address-edit / activate-toggle / delete.
 * Per Q18, v1 fields are: code (auto-generated, editable on create
 * only), name, address, is_active.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  AlertCircle,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
  Warehouse as WarehouseIcon,
  X,
} from "lucide-react";
import { type FormEvent, useState } from "react";

import {
  ApiError,
  type ErpWarehouse,
  type ErpWarehouseCreate,
  type ErpWarehouseUpdate,
  erpApi,
  isErpCatalogConflict,
} from "../lib/api";
import { ERP_QUERY_KEYS, useWarehouses } from "../lib/erp";
import { toast } from "../lib/toast";

export const Route = createFileRoute("/_app/erp/settings/warehouses")({
  beforeLoad: () => {
    throw redirect({ to: "/settings/warehouses" });
  },
  component: WarehousesAdminPage,
});

export function WarehousesAdminPage() {
  const queryClient = useQueryClient();
  const warehousesQ = useWarehouses();
  const warehouses = warehousesQ.data?.items ?? [];

  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.warehouses });
  };

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <WarehouseIcon className="size-5 opacity-70" aria-hidden />
          Warehouses
        </h2>
        <p className="text-sm opacity-60">
          Stock locations referenced by inventory and dispatch. Codes auto-generate as WH-NNN —
          override on create if you'd rather use something descriptive (e.g. <code>MAIN-WH</code>).
        </p>
      </header>

      <CreateWarehouseBar onCreated={onChanged} />

      {warehousesQ.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : warehousesQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load warehouses: {(warehousesQ.error as Error).message}
        </p>
      ) : warehouses.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
          <WarehouseIcon className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
          <p className="text-sm font-medium">No warehouses yet.</p>
          <p className="mt-1 text-sm opacity-60">Add one above to get started.</p>
        </div>
      ) : (
        <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
          {warehouses.map((w) => (
            <WarehouseRow key={w.id} warehouse={w} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateWarehouseBar({ onCreated }: { onCreated: () => void }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (body: ErpWarehouseCreate) => erpApi.warehouses.create(body),
    onSuccess: () => {
      setCode("");
      setName("");
      setAddress("");
      setError(null);
      onCreated();
      toast.success("Warehouse created.");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && isErpCatalogConflict(err.detail)) {
        setError(err.detail.message);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not create warehouse.";
      setError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setError(null);
    const body: ErpWarehouseCreate = { name: name.trim() };
    if (code.trim()) body.code = code.trim();
    if (address.trim()) body.address = address.trim();
    createMutation.mutate(body);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-xl border border-[var(--border)] p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Code</span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={32}
            placeholder="auto"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">
            Name <span className="text-red-500">*</span>
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Address</span>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            maxLength={500}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
      </div>

      {error && (
        <p className="flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
          <AlertCircle className="size-3" aria-hidden />
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={createMutation.isPending || !name.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {createMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Plus className="size-4" aria-hidden />
          )}
          Add warehouse
        </button>
      </div>
    </form>
  );
}

function WarehouseRow({
  warehouse,
  onChanged,
}: {
  warehouse: ErpWarehouse;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(warehouse.name);
  const [address, setAddress] = useState(warehouse.address ?? "");

  const updateMutation = useMutation({
    mutationFn: (body: ErpWarehouseUpdate) => erpApi.warehouses.update(warehouse.id, body),
    onSuccess: () => {
      onChanged();
      setEditing(false);
      toast.success("Saved.");
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not save.";
      toast.error(msg);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => erpApi.warehouses.remove(warehouse.id),
    onSuccess: () => {
      onChanged();
      toast.success("Warehouse deleted.");
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not delete.";
      toast.error(msg);
    },
  });

  const startEdit = () => {
    setName(warehouse.name);
    setAddress(warehouse.address ?? "");
    setEditing(true);
  };

  const submitEdit = () => {
    const body: ErpWarehouseUpdate = {};
    if (name.trim() !== warehouse.name) body.name = name.trim();
    const newAddress = address.trim() || null;
    if (newAddress !== (warehouse.address ?? null)) body.address = newAddress;
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    updateMutation.mutate(body);
  };

  const toggleActive = () => {
    updateMutation.mutate({ is_active: !warehouse.is_active });
  };

  const handleDelete = () => {
    if (window.confirm(`Delete warehouse ${warehouse.code}? This cannot be undone.`)) {
      removeMutation.mutate();
    }
  };

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {editing ? (
          <div className="flex flex-wrap items-start gap-2">
            <span className="rounded-md bg-black/5 px-2 py-1 font-mono text-xs dark:bg-white/10">
              {warehouse.code}
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
              placeholder="Name"
            />
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              maxLength={500}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
              placeholder="Address (optional)"
            />
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-black/5 px-2 py-0.5 font-mono text-xs dark:bg-white/10">
              {warehouse.code}
            </span>
            <span className="font-medium">{warehouse.name}</span>
            {warehouse.address && <span className="text-xs opacity-60">{warehouse.address}</span>}
            {!warehouse.is_active && (
              <span className="rounded-full bg-black/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider opacity-60 dark:bg-white/10">
                Inactive
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {editing ? (
          <>
            <button
              type="button"
              onClick={submitEdit}
              disabled={updateMutation.isPending}
              aria-label="Save"
              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
            >
              {updateMutation.isPending ? (
                <Loader2 className="size-3 animate-spin" aria-hidden />
              ) : (
                <Save className="size-3" aria-hidden />
              )}
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              aria-label="Cancel"
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
            >
              <X className="size-3" aria-hidden />
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={toggleActive}
              disabled={updateMutation.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
            >
              {warehouse.is_active ? "Deactivate" : "Activate"}
            </button>
            <button
              type="button"
              onClick={startEdit}
              aria-label={`Edit ${warehouse.code}`}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
            >
              <Pencil className="size-3" aria-hidden />
              Edit
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={removeMutation.isPending}
              aria-label={`Delete ${warehouse.code}`}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950/40"
            >
              {removeMutation.isPending ? (
                <Loader2 className="size-3 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-3" aria-hidden />
              )}
              Delete
            </button>
          </>
        )}
      </div>
    </li>
  );
}
